import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { FormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { debounceTime, filter, map, mergeMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment/moment';
import { ApiClientConstant, ApiConnector, Table } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../app.config';
import { LocalStorage } from '../../../../services/local-storage-service';
import { InputType } from '../../../../../typings/client/input';
import { Broadcaster } from '../../../../components/broadcaster';
import { RegimenProductChangeHistoryComponent } from './regimen-product-change-history-model/regimen-product-change-history.component';
import { WindowRefService } from '../../../../services/window-ref-service';
import { ValueOf } from '../../../../../typings/server/common';
import { RegimenType } from '../../../../../typings/client/regimen';
import { VoiceNoteAddDialog } from '../../../../components/voice-note-add/voice-note-add.component';
import { ServerAPIRequest, ServerAPIResponse } from '../../../server-api.constants';
import { OnHoldPopupComponent } from '../../../../components/onHoldPopup';
import {
  CONCERN_MAPPINGS, AGE_HORMONAL_MAPPINGS, PREGNANCY_MAPPINGS,
  STEROID_MAPPINGS, SENSITIVE_SKIN_PREFIX,
  CONCERN_MAPPINGS_HI, AGE_HORMONAL_MAPPINGS_HI, PREGNANCY_MAPPINGS_HI,
  STEROID_MAPPINGS_HI, SENSITIVE_SKIN_SUFFIX_HI,
} from './regimen-summary-mappings';
import { FollowUpSopHistoryComponent } from './followup-sop/follow-up-sop-history';
import { OnHoldDetailsOnHoldTypeEnum } from '@cureskin/api-client/src/server-api';
import { HelperService } from '../../../../services/helper-service';

declare interface FollowUpSop {
  name: string;
  shortName: string;
  tests: { name?: string, tests?: Array<string> };
  disabled: boolean;
  changes: Array< { type: string, position?: number, partOfDay: string, instruction?: any, productId: string }>;
  nextFollowUps: Array<FollowUpSop>;
  voiceNote: { en?: string, hi?: string, kn?: string, te?: string, ta?: string, ma?: string, mr?: string, bn?: string };
  treatmentProgress: Array<{ issue: string, concernLanguageString: any, score: number}>;
  audioTemplateName: string;
}

@Component({
    selector: 'regimen-edit', templateUrl: './regimen-edit.html', styleUrls: ['./regimen-edit.scss'],
    standalone: false
})
export class RegimenEditComponent implements OnDestroy {
  autoCompleteProductController: UntypedFormControl = new UntypedFormControl();
  autoCompleteRegimenTagsController: UntypedFormControl = new UntypedFormControl();
  productOptions: Observable<Array<{ name: string; object: any }>>;
  orderConfirmationMessage: string = '';
  userType: string = '';
  regimenObj: any = {};
  actionLog: Array<any> = [];
  regimenClasses: Array<InputType.SelectOption>;
  copyFromRegimenId: string;
  userRoles: Array<string> = [];
  followUpProducts: Array<any> = [];
  urlSplit: Array<string> = [];
  ui: any;
  reasonsToEdit: Array<{ display: string, value: string }> = [];
  defaultReasonsToEdit: Array<{ display: string, value: string }> = [
    { display: 'Default regimenSop Selection', value: 'REGIMEN_SOP_SELECTION' },
    { display: 'Repair Product combination needed not available', value: 'REPAIR_PRODUCT_COMBINATION_NEEDED_NOT_AVAILABLE' },
    { display: 'Incorrect Instructions', value: 'INCORRECT_INSTRUCTIONS' },
    { display: 'Incorrect Facewash', value: 'INCORRECT_FACEWASH' },
    { display: 'Incorrect sunscreen', value: 'INCORRECT_SUNSCREEN' },
    { display: 'Incorrect or missing moisturiser', value: 'INCORRECT_OR_MISSING_MOISTURISER' },
    { display: 'Display Concern Updated', value: 'DISPLAY_CONCERN_UPDATED' },
    { display: 'Other', value: 'OTHER' },
  ];
  editReason: any;
  editDetails: any;
  totalPrice: number = 0;
  priceBeforeEditing: number = 0;
  subscriptions: Array<Subscription>;
  services: Array<InputType.SelectOption> = [];
  dietAndLifestyleList: Array<InputType.SelectOption> = [];
  disableVariant: boolean;
  paymentTypeList: Array<InputType.SelectOption> = [
    { value: ApiClientConstant.Order.PaymentType.COD, display: 'COD' },
    { value: ApiClientConstant.Order.PaymentType.ONLINE, display: 'ONLINE' },
    { value: ApiClientConstant.Order.PaymentType.NA, display: 'NA' },
  ];
  treatmentConcernsOptions: Array<InputType.SelectOption> = [
    // Face concerns
    { value: 'ACNE_OR_PIMPLES', display: 'Acne or Pimples' },
    { value: 'DARK_SPOTS_OR_MARK', display: 'Dark Spots or Mark' },
    { value: 'ACNE_SCARS', display: 'Acne Scars' },
    { value: 'PIGMENTATION', display: 'Pigmentation' },
    { value: 'DULL_SKIN', display: 'Dull Skin' },
    { value: 'UNDER_EYE_DARK_CIRCLES', display: 'Under Eye Dark Circles' },
    { value: 'ANTI_AGING', display: 'Anti Aging' },
    // Hair concerns
    { value: 'HAIR_FALL', display: 'Hair Fall' },
    { value: 'HAIR_LOSS', display: 'Hair Loss' },
    { value: 'DANDRUFF', display: 'Dandruff' },
    { value: 'FRIZZY_HAIR', display: 'Frizzy Hair' },
    { value: 'GREY_HAIR', display: 'Grey Hair' },
    { value: 'HAIR_THINNING', display: 'Hair Thinning' },
    { value: 'REGULAR_HAIR_CARE', display: 'Regular Hair Care' },
  ];
  primaryTreatmentConcern: string = '';
  secondaryTreatmentConcern: string = '';
  generatedSummaryText: string = '';
  generatedHindiSummaryText: string = '';
  hindiSummaryStatus: string = '';
  skinParameterConfig: Array<{ key: string; label: string; expectedRange: string }> = [
    { key: 'sebumBalanceParameters', label: 'Sebum Balance', expectedRange: '4-6' },
    { key: 'inflammationParameters', label: 'Inflammation', expectedRange: '0-2' },
    { key: 'sensitivityParameters', label: 'Sensitivity', expectedRange: '0-2' },
    { key: 'skinDamageParameters', label: 'Skin Damage', expectedRange: '0-2' },
    { key: 'skinRenewalParameters', label: 'Skin Renewal', expectedRange: '5-7' },
  ];
  skinParameterScores: { [key: string]: number | null } = {};
  hairParameterConfig: Array<{ key: string; label: string; expectedRange: string }> = [
    { key: 'hairDensityParameters', label: 'Hair Density', expectedRange: '8-10' },
    { key: 'hairStrengthParameters', label: 'Hair Strength', expectedRange: '8-10' },
    { key: 'scalpOilBalanceParameters', label: 'Scalp Oil Balance', expectedRange: '4-6' },
    { key: 'prematureGreyingParameters', label: 'Premature Greying', expectedRange: '0-2' },
    { key: 'greyingParameters', label: 'Greying', expectedRange: '4-6' },
    { key: 'hairSmoothnessParameters', label: 'Hair Smoothness', expectedRange: '8-10' },
  ];
  hairParameterScores: { [key: string]: number | null } = {};
  extraRegimenInfo: any = {};
  pregnancyOptions: Array<InputType.SelectOption> = [
    { value: 'PREGNANT', display: 'Pregnant' },
    { value: 'NOT_PREGNANT', display: 'Not Pregnant' },
    { value: 'BREASTFEEDING', display: 'Breastfeeding' },
  ];
  hormonalImpactOptions: Array<InputType.SelectOption> = [
    { value: 'PCOD', display: 'PCOD' },
    { value: 'REGULAR_PERIODS', display: 'Regular Periods' },
    { value: 'IRREGULAR_PERIODS', display: 'Irregular Periods' },
  ];
  ageOptions: Array<InputType.SelectOption> = [
    { value: '<17', display: '<17' },
    { value: '17-45', display: '17-45' },
    { value: '>30', display: '>30' },
    { value: '>45', display: '>45' },
  ];
  sensitiveSkinOptions: Array<InputType.SelectOption> = [
    { value: 'YES', display: 'Yes' },
    { value: 'NO', display: 'No' },
  ];
  steroidAbuseOptions: Array<InputType.SelectOption> = [
    { value: 'YES', display: 'Yes' },
    { value: 'NO', display: 'No' },
  ];
  genderOptions: Array<InputType.SelectOption> = [
    { value: 'MALE', display: 'Male' },
    { value: 'FEMALE', display: 'Female' },
  ];
  expectedTimelineOptions: Array<InputType.SelectOption> = [
    { value: '2-3_MONTHS', display: '2-3 Months' },
    { value: '3-4_MONTHS', display: '3-4 Months' },
    { value: '4-5_MONTHS', display: '4-5 Months' },
    { value: '5-6_MONTHS', display: '5-6 Months' },
  ];
  showDisplayConcern: boolean = false;
  showSkinReportParams: boolean = false;
  showHairReportParams: boolean = false;
  showExtraRegimenInfo: boolean = false;
  regimenChange: any = {};
  hideHeader: boolean;
  fromOrderApproval: boolean;
  saveOnProcess: boolean;
  username: string;
  editType: string;
  disableButton: boolean;
  followUpConfig: any = {
    intervalInDays: 28,
    durationInDays: 90,
  };
  mediaLinkObj:any;
  regimenTagOptions: Observable<Array<{ name: string; object: any }>>;
  window: any;
  updateEditReasonRegimenSop: boolean = false;

  // variant changes
  variants: Array<any> = [
    { followUpConfig: this.followUpConfig, variantId: this.appConfig.Shared.Regimen.Variants.VariantId.variant_1499 },
    { followUpConfig: this.followUpConfig,
      variantId: this.appConfig.Shared.Regimen.Variants.VariantId.variant_1999,
      fixedPrice: 2000,
      fixedPriceMRP: 2450,
      numberOfProductsLimit: 5 },
    { followUpConfig: this.followUpConfig,
      variantId: this.appConfig.Shared.Regimen.Variants.VariantId.variant_4999,
      fixedPrice: 3000,
      fixedPriceMRP: 3600,
      numberOfProductsLimit: 5 },
    { followUpConfig: this.followUpConfig,
      variantId: this.appConfig.Shared.Regimen.Variants.VariantId.variant_999,
      fixedPrice: 999,
      fixedPriceMRP: 1250,
      numberOfProductsLimit: 3 },
  ];
  currentVariantId: ValueOf<typeof this.appConfig.Shared.Regimen.Variants.VariantId> = ApiClientConstant.Regimen.SelectedVariantId.variant_1499;
  regimenObjVariant1: any;
  regimenObjVariant2: any;
  regimenObjVariant3: any;
  regimenObjVariant4: any;
  isProductUpdated: boolean = false;
  regimenBeforeChangeProducts:any = [];
  changeMorningProducts: boolean = false;
  changeNightProducts: boolean = false;
  orders: any = [];
  isProductExistedWarningIgnored: boolean = false;
  changedProductsWhichAreAlreadyInOrdersInTransit: any = [];
  changedProductsWhichAreAlreadyInOrdersInDelivered: any = [];
  antibioticsReasonRequired: boolean = false;
  selectedChatType: 'userChat' | 'chatSummary' = 'chatSummary';
  showAsOfflineRegimen: boolean = false;
  onHoldPopupAlreadyShown: boolean = false;
  capturedByDoctor: any;
  currentUser: any;
  canDoctorApproveRegimen: boolean = true;
  patientName: string;
  autoCompleteProgessiveInfo: UntypedFormControl = new UntypedFormControl();
  progressiveInfoProducts: Observable<any>;
  autoCompleteInvestigation: UntypedFormControl = new UntypedFormControl();
  investigations: Array<Table.Investigations> = [];
  selectedInvestigation: Table.Investigations;
  investigationOptions: Observable<Array<{ name: string; object: Table.Investigations }>>;
  progressiveInfoActions: Array<{ displayName: string, value: RegimenType.ProgressiveInfo['action'] }> = [
    { displayName: 'Add to banner', value: ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER },
    { displayName: 'Add to banner with regimen change', value: ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER_WITH_REGIMEN_CHANGE },
  ];
  showProgressiveProductInfo: boolean = false;
  progressiveInfoProductObj: Table.Catalog;
  progressiveInfoForm: FormGroup;
  progressiveInfoInstructionLoading: boolean = false;
  disableProgressiveInfo: boolean = false;
  onHoldProductsPayload: ServerAPIRequest['OnHoldProducts'] = {
    onHoldProducts: [],
    onHoldDurationInDays: 0,
    ticketId: '',
    onHoldType: OnHoldDetailsOnHoldTypeEnum.TICKET
  };
  private originalProductIds: Set<string> = new Set();
  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;

  @Output('afterSaveRegimen') afterSaveRegimen: EventEmitter<any> = new EventEmitter();
  @Output('afterChangeProduct') afterChangeProduct: EventEmitter<any> = new EventEmitter();
  @Input('hideNotNecessaryThings') hideNotNecessaryThings: boolean = false;
  @Input('regimen')
  set processRegimen(regimen: any) {
    if (!regimen) return;
    this.hideHeader = true;
    this.fromOrderApproval = true;
    this.updateRegimen(regimen);
  }
  @Input('editType')
  set processEditReasons(editType: string) {
    this.editType = editType;
    this.initReasons();
  }

  constructor(private router: Router,
    private conn: ConnectionService,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private storage: LocalStorage,
    private broadcast: Broadcaster,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog, windowRef: WindowRefService,
    private broadcaster: Broadcaster,
    private readonly changedetectionRef: ChangeDetectorRef,
    private readonly helperService: HelperService,
  ) {
    this.window = windowRef.nativeWindow;
  }

  async ngOnInit(): Promise<any> {
    this.mediaLinkObj = new Table.MediaLink();
    this.currentUser = this.conn.getCurrentUser();
    this.username = this.currentUser.get('username');
    this.userType = this.currentUser.get('type');
    this.regimenTagOptions = this.autoCompleteRegimenTagsController.valueChanges
      .pipe(
        debounceTime(300),
        filter((token: string) => !!token.length),
        mergeMap((token: string) => this.getLanguageStringTags(token)));
    this.productOptions = this.autoCompleteProductController.valueChanges.pipe(
      debounceTime(300),
      filter((token: string) => !!token.length),
      mergeMap((token: string) => this.getProducts(token)));
    this.progressiveInfoProducts = this.autoCompleteProgessiveInfo.valueChanges.pipe(
      debounceTime(300),
      filter(token => !!token.length),
      mergeMap(token => this.getProducts(token)),
    );
    this.investigationOptions = this.autoCompleteInvestigation.valueChanges.pipe(
      debounceTime(300),
      map((token: string) => {
        if (!token?.length) return [];
        return this.investigations
          .filter((inv: any) => inv.get('name').toLowerCase().includes(token.toLowerCase()))
          .map((inv: any): { name: string; object: any } => ({ name: inv.get('name') as string, object: inv }));
      }),
    );
    this.conn.fetchInvestigations().then((investigations: Array<any>) => {
      this.investigations = investigations;
    });
    this.initReasons();
    this.subscriptions = [];
    this.ui = { addProductFlag: false, makeBlur: false };
    this.reset();
    this.regimenClasses = Object.keys(ApiClientConstant.Regimen.Class)
      .map((key: string) => ({ display: key, value: key }));
    this.userRoles = this.storage.getJsonValue('userRoles');
    this.conn.getServices({
      type: ApiClientConstant.Service.Type.REGIMEN_CONSULTATION,
      ascending: 'createdAt',
    })
      .then((data: any) => (this.services = data.map((service: any): InputType.SelectOption => ({
        display: `${service.get('duration')} &nbsp; weeks &nbsp; ${service.get('amount')} Rupees &nbsp;&nbsp;`,
        value: service.id,
        parseValue: service,
      }))));
    await this.subscribeToUrlQueryParams();
    this.initProgressiveInfoForm();
    this.fetchDietAndLifestyles();
    this.route.queryParamMap.subscribe((queryParams: any): void => {
      const type = queryParams.get('type');
      this.showAsOfflineRegimen = type === 'offline';
      this.hideNotNecessaryThings = type === 'offline';
    });
    if (this.route.parent.snapshot.data.regimen) {
      this.setRegimenBeforeChangeProducts();
    }
    this.canDoctorApproveRegimen = this.checkDoctorCanApproveRegimen();
  }

  checkDoctorCanApproveRegimen(): boolean {
    if (this.capturedByDoctor && this.currentUser.id !== this.capturedByDoctor?.id) {
      this.broadcaster.broadcast('NOTIFY', {
        message: 'Regimen already captured by another doctor',
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
      return false;
    }
    return true;
  }

  setRegimenBeforeChangeProducts():void {
    this.regimenBeforeChangeProducts
      .push([...this.route.parent.snapshot.data.regimen.get('morning').map((product:any) => JSON.parse(JSON.stringify(product))),
        ...this.route.parent.snapshot.data.regimen.get('night').map((product:any) => JSON.parse(JSON.stringify(product)))]);
  }

  openNewTab(url: string): void {
    this.window.open(url, '_blank');
  }
  async getProducts(name: string): Promise<Array<{ name: string; object: any }>> {
    const products = await this.conn.findCatalogs({
      where: { title: { $regex: name, $options: 'i' } },
      limit: 20,
      project: ['title', 'quantity', 'quantityUnit', 'type', 'isRepair'],
    });
    return products.map((product: any): { name: string; object: any } => ({
      name: this.constructProductName(product),
      object: product,
    }));
  }
  async getLanguageStringTags(name: string): Promise<Array<{ name: string; object: any }>> {
    const languageStrings = await this.conn.findLanguageStringTags({
      where: { name: { $regex: name, $options: 'i' } },
      limit: 5,
      project: ['name'],
    });
    return languageStrings.map((languageString: any): { name: string; object: any } => ({
      name: languageString.get('name'),
      object: languageString,
    }));
  }

  autoCompleteProductOnSelect(item: { name: string; object: any }): void {
    this.followUpProducts.push(item.object);
    this.autoCompleteProductController.setValue('');
  }

  removeProduct(index: number): void {
    this.followUpProducts.splice(index, 1);
  }

  onTreatmentConcernChange(): void {
    const displayConcern = this.regimenObj.get('displayConcern');
    const concerns: Array<string> = [];
    if (this.primaryTreatmentConcern) {
      concerns.push(this.primaryTreatmentConcern);
    }
    if (this.secondaryTreatmentConcern) {
      concerns.push(this.secondaryTreatmentConcern);
    }
    displayConcern.treatmentConcerns = concerns;
    this.regimenObj.set('displayConcern', displayConcern);
  }

  onSkinReportParamsChange(): void {
    const skinParameters: any = {};
    this.skinParameterConfig.forEach((param) => {
      let score = this.skinParameterScores[param.key];
      if (score != null && Number(score) < 0) {
        score = 0;
        this.skinParameterScores[param.key] = 0;
      }
      if (score != null) {
        skinParameters[param.key] = `{patientScore:${Number(score)},expectedRange:${param.expectedRange}}`;
      } else {
        skinParameters[param.key] = '';
      }
    });
    this.regimenObj.set('skinReportParams', {
      skinParameters,
    });
  }

  onHairReportParamsChange(): void {
    const hairParameters: any = {};
    this.hairParameterConfig.forEach((param) => {
      let score = this.hairParameterScores[param.key];
      if (score != null && Number(score) < 0) {
        score = 0;
        this.hairParameterScores[param.key] = 0;
      }
      if (score != null) {
        hairParameters[param.key] = `{patientScore:${Number(score)},expectedRange:${param.expectedRange}}`;
      } else {
        hairParameters[param.key] = '';
      }
    });
    this.regimenObj.set('hairReportParams', { hairParameters });
  }

  onExtraRegimenInfoChange(): void {
    this.regimenObj.set('extraRegimenInfo', { ...this.extraRegimenInfo });
  }

  generateOverallSummary(): void {
    // 1. Class prefix
    const regimenClass = (this.regimenObj.get('class') || '').toLowerCase();
    const classPrefix = regimenClass === 'hair' ? 'You are facing' : 'Your skin shows';

    // 2. Concern mapping lookup: try combined key first, then fall back to primary
    const dc = this.regimenObj.get('displayConcern') || {};
    const treatmentConcerns: Array<string> = dc.treatmentConcerns || [];
    const combinedKey = treatmentConcerns.join(',');
    const concernMapping = CONCERN_MAPPINGS[combinedKey]
      || CONCERN_MAPPINGS[treatmentConcerns[0]]
      || { concernText: '', reasonText: '', expectedOutcome: '' };

    // 3. Age + Hormonal mapping
    const age = this.extraRegimenInfo.age || '';
    const hormonal = this.extraRegimenInfo.hormonalImpact || '';
    const ageHormonalKey = `${age}_${hormonal}`;
    const ageHormonalText = (age && hormonal) ? (AGE_HORMONAL_MAPPINGS[ageHormonalKey] || '') : '';

    // 4. Build "possibly from" section
    const possiblyFromParts: Array<string> = [];
    if (ageHormonalText) possiblyFromParts.push(ageHormonalText);
    if (concernMapping.reasonText) possiblyFromParts.push(concernMapping.reasonText);
    const possiblyFromSection = possiblyFromParts.length > 0
      ? ` - possibly from ${possiblyFromParts.join(', ')}` : '';

    // 5. Pregnancy + Steroid: "potentially influenced by" section
    const pregnancyText = PREGNANCY_MAPPINGS[this.extraRegimenInfo.pregnancy] || '';
    const steroidText = STEROID_MAPPINGS[this.extraRegimenInfo.steroidAbuse] || '';
    const influencedByParts: Array<string> = [];
    if (pregnancyText) influencedByParts.push(pregnancyText);
    if (steroidText) influencedByParts.push(steroidText);
    const influencedBySection = influencedByParts.length > 0
      ? `, potentially influenced by ${influencedByParts.join(' and ')}` : '';

    // 6. Sensitive skin prefix
    const sensitiveSkinPrefix = SENSITIVE_SKIN_PREFIX[this.extraRegimenInfo.sensitiveSkin] || 'A';

    // 7. Build full summary
    this.generatedSummaryText = `${classPrefix} ${concernMapping.concernText}${possiblyFromSection}${influencedBySection}. ${sensitiveSkinPrefix} doctor designed routine can ${concernMapping.expectedOutcome}`;
  }

  generateOverallSummaryHindi(): void {
    // 1. Concern mapping lookup (same key logic as English)
    const dc = this.regimenObj.get('displayConcern') || {};
    const treatmentConcerns: Array<string> = dc.treatmentConcerns || [];
    const combinedKey = treatmentConcerns.join(',');
    const concernMapping = CONCERN_MAPPINGS_HI[combinedKey]
      || CONCERN_MAPPINGS_HI[treatmentConcerns[0]]
      || { concernText: '', reasonText: '', expectedOutcome: '' };

    // 2. Age + Hormonal mapping
    const age = this.extraRegimenInfo.age || '';
    const hormonal = this.extraRegimenInfo.hormonalImpact || '';
    const ageHormonalKey = `${age}_${hormonal}`;
    const ageHormonalText = (age && hormonal) ? (AGE_HORMONAL_MAPPINGS_HI[ageHormonalKey] || '') : '';

    // 3. Pregnancy + Steroid
    const pregnancyText = PREGNANCY_MAPPINGS_HI[this.extraRegimenInfo.pregnancy] || '';
    const steroidText = STEROID_MAPPINGS_HI[this.extraRegimenInfo.steroidAbuse] || '';
    const influencedByParts: Array<string> = [];
    if (pregnancyText) influencedByParts.push(pregnancyText);
    if (steroidText) influencedByParts.push(steroidText);
    const influencedBySection = influencedByParts.length > 0
      ? `, ${influencedByParts.join(' और ')}` : '';

    // 4. Sensitive skin suffix
    const sensitiveSkinSuffix = SENSITIVE_SKIN_SUFFIX_HI[this.extraRegimenInfo.sensitiveSkin]
      || ' हो सकते हैं।';

    // 5. Build full Hindi summary
    this.generatedHindiSummaryText = `आपको ${concernMapping.concernText} की समस्या है — इसका मुख्य कारण ${ageHormonalText}${concernMapping.reasonText}${influencedBySection}${sensitiveSkinSuffix} डॉक्टर द्वारा तैयार की गई रूटीन ${concernMapping.expectedOutcome}`;
    this.hindiSummaryStatus = '';
  }

  async applyHindiSummaryToLanguageString(): Promise<void> {
    const overallSummary = this.regimenObj.get('overallSummary');
    if (!overallSummary || !overallSummary.id) {
      this.hindiSummaryStatus = 'No LanguageString found. Please create one with English text first.';
      return;
    }
    try {
      if (!overallSummary.get('en')) {
        await overallSummary.fetch();
      }
      if (!overallSummary.get('en')) {
        this.hindiSummaryStatus = 'LanguageString exists but has no English text. Please add English first.';
        return;
      }
      overallSummary.set('hi', this.generatedHindiSummaryText);
      await overallSummary.save();
      this.hindiSummaryStatus = 'Hindi summary saved to LanguageString successfully.';
    } catch (error) {
      this.hindiSummaryStatus = `Error saving Hindi summary: ${error.message}`;
    }
  }

  initReasons(): void {
    if (this.editType === AppConfig.Shared.RegimenSop.RegimenSop) {
      this.reasonsToEdit = this.defaultReasonsToEdit;
      return;
    }
    this.reasonsToEdit = [
      { display: 'Progress unsatisfactory: Changing/adding repair products', value: 'PROGRESS_UNSATISFACTORY_ADDING_REPAIR_PRODUCTS' },
      { display: 'Progress unsatisfactory: Changing/adding oral medications', value: 'PROGRESS_UNSATISFACTORY_ADDING_ORAL_MEDICATIONS' },
      { display: 'Progress Satisfactory: Moving to next concern (display concern updated)',
        value: 'PROGRESS_SATISFACTORY_MOVING_TO_NEXT_CONCERN' },
      { display: 'Different concern', value: 'DIFFERENT_CONCERN' },
      { display: 'Progress satisfactory: Moving to maintenance', value: 'PROGRESS_SATISFACTORY_MAINTENANCE' },
      { display: 'Progress satisfactory: Moving to milder regimen', value: 'PROGRESS_SATISFACTORY_MILDER' },
      { display: 'User complaints: Moving to milder variant', value: 'USER_COMPLAINT_MILDER' },
      { display: 'Continue Maintenance', value: 'CONTINUE_MAINTENANCE' },
      { display: 'Other', value: 'OTHER' },
      { display: 'Instruction correction/change.', value: 'INSTRUCTION_CORRECTION' },
      { display: 'Additional concerns addressed', value: 'ADDITIONAL_CONCERNS_ADDRESSED' },
      { display: 'Add on product added', value: 'ADD_ON_PRODUCT_ADDED' },
    ];
  }

  subscribeToUrlQueryParams(): void {
    this.subscriptions.push(this.route.queryParams.subscribe((queryParams: Params) => {
      if (queryParams.copy) {
        this.conn.getRegimen(queryParams.copy)
          .then((regimen: any) => {
            this.copyFromRegimen(regimen, true);
            this.updateRegimen(this.regimenObj);
          });
      }
    }));
    this.subscriptions.push(this.route.parent.params.subscribe(async () => {
      if (this.route.parent.snapshot.data.alternateRegimen) {
        this.updateRegimen(this.route.parent.snapshot.data.alternateRegimen);
      } else if (this.route.parent.snapshot.data.regimen) {
        if (this.isAlternateRegimen()) {
          this.copyFromRegimen(this.route.parent.snapshot.data.regimen, true);
          this.regimenObj.set('regimenId', this.route.parent.snapshot.data.regimen.get('regimenId'));
          this.regimenObj.set('class', this.route.parent.snapshot.data.regimen.get('class'));
          this.updateRegimen(this.regimenObj);
          return;
        }
        this.updateRegimen(this.route.parent.snapshot.data.regimen);
      }
    }));
    this.disableProgressiveInfo = this.regimenObj?.get('type') === this.appConfig.Shared.Regimen.Type.PERSONALIZED;
  }

  async setRegimenCoverImage(products: Array<any>, updateCoverImage: boolean = false): Promise<void> {
    const combinedProductIds: string = this.getCombinedProductIds(products);
    this.mediaLinkObj = await ApiConnector.findOne(Table.MediaLink,
      {
        where: { uniqueId: combinedProductIds },
        project: ['objectId', 'link'],
      });
    if (this.mediaLinkObj) {
      if (updateCoverImage) {
        this.regimenObj.set('regimenCoverImage', this.mediaLinkObj.get('link'));
      }
      this.openNewTab(`${this.conn.getBaseUrl()}/products/medialink/${this.mediaLinkObj?.id}/edit`);
      return;
    }
    this.openNewTab(`${this.conn.getBaseUrl()}/products/medialink/new`);
  }

  async getImageUrlforCombinedProduct(combineProductId: string): Promise<string> {
    if (!combineProductId) return '';
    const [mediaLink]: any = await this.conn.getMediaLink({ where: { uniqueId: combineProductId } });
    if (!mediaLink) return '';
    return mediaLink.get('link');
  }

  getCombinedProductIds(products: Array<any>): string {
    if (!products?.length) return '';
    const productIds = products.map((product: any) => product.id);
    return `regimenCoverPhoto_${productIds.sort().join('_')}`;
  }

  async openMediaLinkEditForCurrentProducts(): Promise<void> {
    if (!this.currentVariantId) {
      await this.setRegimenCoverImage(this.regimenObj.get('products'));
      return;
    }
    const products = this.getAllUniqueProducts([...this.regimenObj.get('morning'), ...this.regimenObj.get('night')]);
    await this.setRegimenCoverImage(products, true);
  }

  updateRegimen(regimen: any): void {
    this.capturedByDoctor = regimen.get('capturedByDoctor');
    this.regimenObj = regimen;
    if (this.regimenObj.has('followUpConfig')) {
      this.followUpConfig = this.regimenObj.get('followUpConfig');
    }
    // Initialize displayConcern if it doesn't exist
    if (!this.regimenObj.get('displayConcern')) {
      this.regimenObj.set('displayConcern', {});
    }
    // Populate primary/secondary from treatmentConcerns array for UI
    const dc = this.regimenObj.get('displayConcern');
    if (Array.isArray(dc.treatmentConcerns)) {
      this.primaryTreatmentConcern = dc.treatmentConcerns[0] || '';
      this.secondaryTreatmentConcern = dc.treatmentConcerns[1] || '';
    }
    // Initialize skinReportParams
    if (!this.regimenObj.get('skinReportParams')) {
      this.regimenObj.set('skinReportParams', { skinParameters: {} });
    }
    const srp = this.regimenObj.get('skinReportParams');
    const sp = srp.skinParameters || {};
    this.skinParameterScores = {};
    this.skinParameterConfig.forEach((param) => {
      const paramData = sp[param.key];
      if (paramData && typeof paramData === 'string') {
        const match = paramData.match(/patientScore:(\d+)/);
        this.skinParameterScores[param.key] = match ? Number(match[1]) : null;
      } else if (paramData && typeof paramData === 'object' && paramData.patientScore != null) {
        this.skinParameterScores[param.key] = paramData.patientScore;
      } else {
        this.skinParameterScores[param.key] = null;
      }
    });
    // Initialize hairReportParams
    if (!this.regimenObj.get('hairReportParams')) {
      this.regimenObj.set('hairReportParams', { hairParameters: {} });
    }
    const hrp = this.regimenObj.get('hairReportParams');
    const hp = hrp.hairParameters || {};
    this.hairParameterScores = {};
    this.hairParameterConfig.forEach((param) => {
      const paramData = hp[param.key];
      if (paramData && typeof paramData === 'string') {
        const match = paramData.match(/patientScore:(\d+)/);
        this.hairParameterScores[param.key] = match ? Number(match[1]) : null;
      } else if (paramData && typeof paramData === 'object' && paramData.patientScore != null) {
        this.hairParameterScores[param.key] = paramData.patientScore;
      } else {
        this.hairParameterScores[param.key] = null;
      }
    });
    // Initialize extraRegimenInfo
    if (!this.regimenObj.get('extraRegimenInfo')) {
      this.regimenObj.set('extraRegimenInfo', {});
    }
    this.extraRegimenInfo = { ...this.regimenObj.get('extraRegimenInfo') };
    this.regimenChange.concernsLanguageString = this.regimenObj?.get('concernsLanguageString');
    this.regimenChange.originalProducts = [...this.regimenObj.get('morning'), ...this.regimenObj.get('night')];
    this.originalProductIds = new Set([
      ...this.regimenObj.get('morning').map((each: any) => each.product.id),
      ...this.regimenObj.get('night').map((each: any) => each.product.id),
    ]);
    this.updateVariantsDetails();
    this.findRegimenChangeLog();
    this.updateRegimenType();
    this.updatePrice();
    this.priceBeforeEditing = this.totalPrice;
    this.patientName = this.regimenObj?.get('forUser')?.get('PatientName');
    this.loadSelectedInvestigation(regimen);
    setTimeout(() => (this.regimenObj = regimen), 0);
  }

  private async loadSelectedInvestigation(regimen: any): Promise<void> {
    const inv = regimen.get('investigation');
    if (!inv) {
      this.selectedInvestigation = null;
      this.autoCompleteInvestigation.setValue('', { emitEvent: false });
      return;
    }
    if (inv.get('name')) {
      this.selectedInvestigation = inv;
      this.autoCompleteInvestigation.setValue(inv.get('name') as string, { emitEvent: false });
      return;
    }
    const fromList = this.investigations.find((each: any) => each.id === inv.id);
    if (fromList) {
      this.selectedInvestigation = fromList;
      this.autoCompleteInvestigation.setValue(fromList.get('name') as string, { emitEvent: false });
      return;
    }
    await inv.fetch();
    this.selectedInvestigation = inv;
    this.autoCompleteInvestigation.setValue(inv.get('name') as string, { emitEvent: false });
  }

  onInvestigationSelect(option: { name: string; object: any }): void {
    this.selectedInvestigation = option.object;
    this.regimenObj.set('investigation', option.object);
  }

  clearInvestigation(): void {
    this.selectedInvestigation = null;
    this.autoCompleteInvestigation.setValue('', { emitEvent: false });
    this.regimenObj.unset('investigation');
  }

  async onProductUpdate(): Promise<void> {
    this.afterChangeProduct.emit('regimenEdited');
    this.disableFollowUpSop();
    this.updatePrice();
    this.isProductUpdated = true;
  }

  addProductInOnHoldProductsPayload(onHoldProducts: Table.Regimen['attributes']['morning']): void {
    onHoldProducts.forEach((onHoldProduct) => {
      this.onHoldProductsPayload.onHoldProducts.push({
        onHold: true,
        productId: onHoldProduct.product.id,
        instructionCreationId: onHoldProduct.instructionSet.creationId,
        partOfDay: onHoldProduct.instructionSet.partOfDay,
      });
    })
  }

  disableFollowUpSop(): void {
    if (this.regimenObj.get('type') !== this.appConfig.Shared.Regimen.Type.PERSONALIZED) return;
    if (this.regimenObj.dirtyKeys().includes('morning') || this.regimenObj.dirtyKeys().includes('night')) {
      this.regimenObj.set('disableFollowUpSop', true);
    }
  }

  updatePrice(): void {
    const morning = this.regimenObj.get('morning');
    const night = this.regimenObj.get('night');
    const morningNight = [].concat(morning || []).concat(night || []);
    this.totalPrice = 0;
    const uniqueProducts = [];
    morningNight.forEach((item: any) => {
      if (!item.product || uniqueProducts.includes(item.product.id)) {
        return;
      }
      this.totalPrice += item.product.get('price');
      uniqueProducts.push(item.product.id);
    });
    if (this.regimenObj.get('consultationService')) {
      this.totalPrice += this.regimenObj.get('consultationService').get('amount');
    }
  }

  updateRegimenEditReason(): void {
    this.editReason = '';
    if (this.editType === this.appConfig.Shared.RegimenSop.RegimenSop && !this.updateEditReasonRegimenSop) {
      this.reasonsToEdit = this.defaultReasonsToEdit.splice(1);
      this.updateEditReasonRegimenSop = true;
    }
  }

  findRegimenChangeLog(): void {
    if (!this.regimenObj.id) return;
    this.conn.getActionLog({ regimen: this.regimenObj })
      .then((actionLog: Array<any>) => (this.actionLog = actionLog));
  }

  reset(): void {
    this.regimenObj = new Table.Regimen();
    this.regimenObj.set('morning', []);
    this.regimenObj.set('night', []);
    this.updateRegimenType();
  }

  updateRegimenType(): void {
    if (this.regimenObj.get('type')) return;
    if (this.isAlternateRegimen()) {
      this.regimenObj.set('type', ApiClientConstant.Regimen.Type.ALTERNATE);
      return;
    }
    this.regimenObj.set('type', ApiClientConstant.Regimen.Type.MAIN);
  }

  isAlternateRegimen(): boolean {
    this.urlSplit = this.router.url.split('/');
    if (this.urlSplit[this.urlSplit.length - 2] === 'alternates') return true;
    return this.urlSplit[this.urlSplit.length - 3] === 'alternate';
  }

  async saveRegimen(context?: any): Promise<any> {
    this.disableVariant = false;
    const variantsUpdated = await this.updateVariants();
    this.syncMainRegimenValuesToVariant();
    if (!variantsUpdated) {
      return;
    }
    this.checkInstructionsExists();
    if (this.disableButton) {
      this.broadcast.broadcast('NOTIFY', { message: 'after approval regimen cannot be updated',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return;
    }
    const isEditAllowed = await this.checkIfRegimenEditAllowed();
    if (!isEditAllowed) {
      this.saveOnProcess = false;
      return;
    }
    if (!this.userRoles.includes('regimenEditor')) {
      alert('Regimen Edit permission is missing.');
      return;
    }
    this.saveOnProcess = true;
    const isNew = !this.regimenObj.id;
    if (isNew) {
      this.resetFollowUpSopAudioTemplate();
    }
    const reason = {
      editReason: this.editReason,
      editDetails: this.editDetails,
    };
    let valid;
    this.regimenObj.set('regimenEditReason', reason);
    const result = await this.isWhenToUseHavingSameDay();
    if (valid) {
      this.saveOnProcess = false;
      return;
    }
    if (!this.editReason) {
      this.saveOnProcess = false;
      this.broadcast.broadcast('NOTIFY', { message: 'Regimen Edit Reason Should not be blank',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return;
    }
    const repairProductCheck = await this.repairShouldNotBeInMorning();
    if (!result || !repairProductCheck) return;
    const newProductAddedInRegimen = [...this.regimenObj.get('morning'), ...this.regimenObj.get('night')]
      .some((each: any) => !this.originalProductIds.has(each.product.id));
    if (newProductAddedInRegimen) {
      valid = await this.isRepairProductEditedButDisplayConcernNotChanged();
      if (!valid) {
        this.saveOnProcess = false;
        return;
      }
    }
    if (this.antibioticsReasonRequired && this.regimenObj?.get('continueAntibioticsReason')?.length < 15) {
      this.broadcast.broadcast('NOTIFY', { message: 'For continue antibiotics give reason in more than 15 characters',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      this.saveOnProcess = false;
      return;
    }
    this.removeUpdatedFlag();
    if (this.regimenObj.get('numberOfProductsLimit') && !isNaN(this.regimenObj.get('numberOfProductsLimit'))) {
      this.regimenObj.set('numberOfProductsLimit', this.regimenObj.get('numberOfProductsLimit'));
    }
    this.regimenObj.set('followUpConfig', this.followUpConfig);
    if (this.isProductUpdated && !this.isProductExistedWarningIgnored && this.regimenObj?.id && this.regimenObj.has('forUser')) {
      await this.checkChangedProductsHasAlreadyInOrder();
      if (this.changedProductsWhichAreAlreadyInOrdersInDelivered?.length
        || this.changedProductsWhichAreAlreadyInOrdersInTransit?.length) {
        return;
      }
    }
    const formValue = this.progressiveInfoForm.value;
    let progressiveInfo: Table.Regimen['attributes']['progressiveInfo'] = [];
    if (formValue.product && formValue.startAfterDayOfDelivery && formValue.endAfterDayOfDelivery) {
      const isProductInfoPresent = Object.keys(formValue.productInfo).map((key: any) => formValue.productInfo[key]).some((each: any) => each);
      const shouldAddProductInfo = formValue.action === ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER_WITH_REGIMEN_CHANGE;

      const progressiveInfoItem: Table.Regimen['attributes']['progressiveInfo'][0] = {
        product: formValue.product,
        action: formValue.action,
        startAfterDayOfDelivery: formValue.startAfterDayOfDelivery,
        endAfterDayOfDelivery: formValue.endAfterDayOfDelivery,
        status: formValue.status
      };
      if (shouldAddProductInfo && isProductInfoPresent) {
        const productName = this.progressiveInfoProductObj ? this.constructProductName(this.progressiveInfoProductObj) : '';
        const existingProgressiveInfo = this.regimenObj.get('progressiveInfo')?.[0];
        const existingProductInfo = existingProgressiveInfo?.productInfo;
        const currentDate = new Date();

        progressiveInfoItem.productInfo = {
          instructionSet: formValue.selectedInstructionSet,
          purposeDescriptionLanguageString: formValue.productInfo.purposeDescriptionLanguageString,
          purposeLanguageString: formValue.productInfo.purposeLanguageString,
          onHold: formValue.productInfo.onHold,
          productName,
          createdAt: existingProductInfo?.createdAt ?? currentDate,
          updatedAt: existingProductInfo?.updatedAt ?? currentDate,
        };

        // Update the updatedAt time only if there are actual changes
        if (existingProductInfo) {
          const hasInstructionChanged = existingProductInfo.instructionSet?.id !== formValue?.selectedInstructionSet?.id;
          const hasOnHoldChanged = existingProductInfo.onHold !== formValue?.productInfo?.onHold;
          const hasProductNameChanged = existingProductInfo.productName !== productName;

          if (!(hasInstructionChanged || hasOnHoldChanged || hasProductNameChanged)) {
            progressiveInfoItem.productInfo.updatedAt = existingProductInfo.updatedAt;
          }
        }
      }

      progressiveInfo = [progressiveInfoItem];
      this.regimenObj.set('progressiveInfo', progressiveInfo);
    }
    const onHoldProducts = [...this.regimenObj.get('morning'), ...this.regimenObj.get('night')].filter(product=> product.onHold);
    if (this.regimenObj.get('type') === ApiClientConstant.Regimen.Type.PERSONALIZED
      && (this.regimenObj.dirtyKeys().includes('morning') || this.regimenObj.dirtyKeys().includes('night'))
      && this.currentUser.get('type') === ApiClientConstant.User.Type.DOCTOR
      && !this.onHoldPopupAlreadyShown
      && !newProductAddedInRegimen && onHoldProducts.length) {
      const popupResult = await this.openOnHoldPopup();
      if (!popupResult) {
        // User cancelled or provided invalid data - abort the entire save procedure
        this.saveOnProcess = false;
        return;
      }
      this.addProductInOnHoldProductsPayload(onHoldProducts);
      const response = await this.upsertOnHoldProducts(this.regimenObj.id, this.onHoldProductsPayload);
      if (response.status === 200) {
        this.broadcast.broadcast('NOTIFY', { message: 'On Hold Products Updated', type: this.appConfig.Shared.Toast.Type.SUCCESS });
        const morningCopy = response.data.data.regimen.morning;
        const nightCopy = response.data.data.regimen.night;
        this.updateRegimenFromAPIResponse(morningCopy, nightCopy, this.regimenObj.get('morning'), this.regimenObj.get('night'))
        this.regimenObj.set('morning', morningCopy);
        this.regimenObj.set('night', nightCopy);
        this.onHoldPopupAlreadyShown = true;
      } else {
        this.broadcast.broadcast('NOTIFY', { message: 'Failed to update on hold products', type: this.appConfig.Shared.Toast.Type.ERROR });
        this.onHoldPopupAlreadyShown = false;
      }
    }
    try {
      await this.regimenObj.save({}, { context });
      this.saveOnProcess = false;
      this.antibioticsReasonRequired = false;
      this.broadcast.broadcast('NOTIFY', { message: 'Regimen Updated', type: this.appConfig.Shared.Toast.Type.SUCCESS });
      if (this.fromOrderApproval) {
        this.afterSaveRegimen.emit(this.regimenObj);
        return;
      }
      if (this.isAlternateRegimen()) {
        this.router.navigate(
          [`${isNew ? '../' : ''}../../alternate`, this.regimenObj.get('regimenId')],
          { relativeTo: this.route });
        return;
      }
      this.router.navigate(
        [`${isNew ? '../' : ''}../../regimen`, this.regimenObj.get('regimenId')],
        { relativeTo: this.route });
    } catch (err: any) {
      this.saveOnProcess = false;
      if (err.code === 422) {
        if (err.message === 'Not more than two months antibiotics allowed in regimen') {
          if (confirm('Avoid antibiotics for over 2 months. If needed, provide a reason.')) {
            this.antibioticsReasonRequired = true;
            // eslint-disable-next-line consistent-return
            return err;
          }
        }
        if (err.message === 'Not more than two repair products allowed') {
          this.broadcast.broadcast('NOTIFY',
            { message: 'More Than Two Repair Products Not Allowed', type: this.appConfig.Shared.Toast.Type.ERROR });
          return;
        }
        if (err.message.products) {
          this.followUpProducts = await this.conn
            .getProducts({ where: { objectId: err.message.products.map((each: any) => each.id) } });
        }
        this.regimenObj.set('followUpNeeded', true);
        this.regimenObj.set('followUpRequired', true);
        this.orderConfirmationMessage = err.message.message;
        await this.showDialogForCreateOrderConfirmation();
        // eslint-disable-next-line consistent-return
        return err;
      }
      alert(err.message.message || err.message);
      // eslint-disable-next-line consistent-return
      return err;
    }
  }

  async productExistedInPreviousOrder(): Promise<void> {
    this.isProductExistedWarningIgnored = true;
    await this.saveRegimen();
  }

  checkWhichProductChanged():void {
    const regimenMorningProduct = this.regimenObj.get('morning').map((product:any) => product.product.get('margId'));
    const regimenNightProduct = this.regimenObj.get('night').map((product:any) => product.product.get('margId'));
    const editedRegimenAllProductIds:any = [...regimenMorningProduct, ...regimenNightProduct];
    const oldRegimenAllProductIds:any = this.regimenBeforeChangeProducts[0].map((product:any) => product.product.margId);
    return editedRegimenAllProductIds.filter((item:any) => !oldRegimenAllProductIds.includes(item));
  }

  resetPopUp():void {
    this.changedProductsWhichAreAlreadyInOrdersInTransit.length = 0;
    this.changedProductsWhichAreAlreadyInOrdersInDelivered.length = 0;
    this.window.location.reload();
  }

  async checkChangedProductsHasAlreadyInOrder(): Promise<void> {
    try {
      const localProductMap = new Map();
      const startDate = moment().subtract(15, 'day').startOf('day').add(6, 'hours')
        .toDate();
      const endDate = moment().startOf('day').add(1, 'day').toDate();
      const where: any = {};
      where.user = this.regimenObj.get('forUser');
      where.createdAt = { $gte: startDate, $lte: endDate };
      where.isPartialOrder = false;
      where.stage = { $nin: ['RETURN_INITIATED',
        'RETURN_REQUESTED',
        'ONLINE_PAYMENT_PENDING',
        'ONLINE_PAYMENT_SUCCESS',
        'INITIAL',
        'RETURNED',
        'CANCELED',
        'LOST_IN_TRANSIT'] };
      this.orders = await this.conn.getOrders({ where });
      const productIds:any = this.checkWhichProductChanged();
      await Promise.all(this.orders.map(async (order:any) => {
        await this.updateIfProductsHasAlreadyInOrder(order, productIds, localProductMap);
      }));
    } catch (error) {
      this.changedProductsWhichAreAlreadyInOrdersInDelivered.length = 0;
      this.changedProductsWhichAreAlreadyInOrdersInTransit.length = 0;
    }
  }

  async updateIfProductsHasAlreadyInOrder(order:any, productIds:any, localProductMap:any): Promise<void> {
    await Promise.all(order.get('products').map(async (product: any) => {
      await this.conn.fetchObject(product, 'title');
      if (!productIds.includes(product.get('margId'))) {
        order.get('products').splice(order.get('products').indexOf(product), 1);
      } else if (!localProductMap.has(product.get('margId'))) {
        localProductMap.set(product.get('margId'), {});
        if (order.get('stage') === 'DELIVERED') {
          this.changedProductsWhichAreAlreadyInOrdersInDelivered.push(product);
        } else {
          this.changedProductsWhichAreAlreadyInOrdersInTransit.push(product);
        }
      }
    }));
  }

  async isWhenToUseHavingSameDay(): Promise<any> {
    const productsInRegimen: Array<any> = this.regimenObj.get('morning').concat(this.regimenObj.get('night'));
    const map = {};
    const whenToApply = productsInRegimen
      .map((item: any) => {
        if (!item?.instructionSet?.frequencyOfApplicationLanguageString?.get('en')) return null;
        return item?.instructionSet?.frequencyOfApplicationLanguageString?.get('en').split(',');
      })
      .filter((item: any) => item !== null)
      .filter((item: any) => item.includes('Monday') || item.includes('Tuesday'));
    const isDuplicate: boolean = whenToApply.some((item: any): boolean => item.some((i: any) => {
      const bool = !!map[i];
      map[i] = true;
      return bool;
    }));
    if (isDuplicate) return confirm('Two or more alternate day products have same days of application. are you sure?');
    return true;
  }

  async isRepairProductEditedButDisplayConcernNotChanged(): Promise<boolean> {
    if (!this.regimenChange?.originalProducts) return true;
    let isRepairProductsChanged = false;
    await Promise.all([
      ...this.regimenChange.originalProducts,
      ...this.regimenObj.get('morning'),
      ...this.regimenObj.get('night'),
    ].map((each: any) => {
      this.conn.fetchObject(each?.product?.get('purposeLanguageString'), 'en');
    }));
    const repairProductsBeforeEdit = this.regimenChange.originalProducts
      .filter((each: any): boolean => {
        const purpose = each.product.get('purpose') || each.product.get('purposeLanguageString')?.get('en');
        return purpose?.toLowerCase().includes('repair');
      }).map((each: any) => each.id);
    const repairProductsAfterEdit = [...this.regimenObj.get('morning'), ...this.regimenObj.get('night')]
      .filter((each: any): boolean => {
        const purpose = each.product.get('purpose') || each.product.get('purposeLanguageString')?.get('en');
        return purpose?.toLowerCase().includes('repair');
      }).map((each: any) => each.id);
    repairProductsBeforeEdit.forEach((each: any) => {
      if (!repairProductsAfterEdit.includes(each)) isRepairProductsChanged = true;
    });
    if ((isRepairProductsChanged || repairProductsAfterEdit.length !== repairProductsBeforeEdit.length)
      && this.isSameArrayOfParseObject(this.regimenObj.get('concernsLanguageString'), this.regimenChange.concernsLanguageString)) {
      return confirm('Are you sure to continue without changing display concern?');
    }
    return true;
  }

  async checkIfRegimenEditAllowed(): Promise<boolean> {
    if (this.regimenObj.get('type') !== ApiClientConstant.Regimen.Type.MAIN) return true;
    const alternateRegimens = JSON.parse(JSON.stringify(await ApiConnector.find(Table.Regimen, {
      where: {
        type: ApiClientConstant.Regimen.Type.ALTERNATE,
        active: true,
        regimenId: { $regex: `${this.regimenObj.get('regimenId')}_`, $options: 'i' },
      },
      project: ['active', 'regimenId'],
    })));
    if (alternateRegimens?.length) {
      this.broadcast.broadcast('NOTIFY', { message: 'Active Alternate Regimen Found',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return false;
    }
    const activeExperiment = await ApiConnector.findOne(Table.Experiment, {
      where: { key: `regimen_${this.regimenObj.get('regimenId')}`, enable: true },
      project: ['key'],
    });
    if (activeExperiment?.id) {
      this.broadcast.broadcast('NOTIFY', { message: 'Active Experiment Found For This Regimen',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return false;
    }
    return true;
  }

  async repairShouldNotBeInMorning(): Promise<any> {
    const productsInRegimen: Array<any> = this.regimenObj.get('morning');
    const foundInMorning = productsInRegimen.find((each: any) => {
      if (!each.updated || !each.purposeLanguageString) return false;
      return each.purposeLanguageString.get('en')?.toLowerCase().includes('repair');
    });
    if (foundInMorning) {
      return confirm('Repair products found in morning section. Are you sure to continue?');
    }
    return true;
  }

  async showDialogForCreateOrderConfirmation(): Promise<any> {
    alert('New Products has been added, So needed to create new Order');
    this.regimenObj.set('paymentType', ApiClientConstant.Order.PaymentType.ONLINE);
    this.ui.makeBlur = true;
  }

  async saveRegimenCreatingOrder(placeOrderForNewProduct: boolean): Promise<any> {
    this.regimenObj.set('placeOrderForNewProduct', placeOrderForNewProduct);
    if (placeOrderForNewProduct && this.followUpProducts.length) {
      this.regimenObj.set('productIds', this.followUpProducts.map((each: any) => each.id));
    }
    this.ui.makeBlur = false;
    const context = { placeOrderForNewProduct };
    await this.saveRegimen(context);
  }

  stopPropagation(e: any): void {
    e.stopPropagation();
  }

  async copyRegimen(skipConfirmation?: boolean): Promise<any> {
    try {
      if (this.copyFromRegimenId.includes('personalized')) {
        await Promise.reject(new Error('Can\'t use personalized regimen to copy'));
      }
      if (this.copyFromRegimenId.split('_').length === 1) {
        this.copyFromRegimenId = `${this.regimenObj.get('regimenId').split('_')[0]}_${this.copyFromRegimenId}`;
      }
      const regimen = await this.conn.getRegimen(this.copyFromRegimenId, false, this.regimenObj.get('class'));
      if (!regimen) await Promise.reject(new Error('No Regimen Found'));
      if (!skipConfirmation && !confirm(`Are you sure to override regimen with ${regimen.get('regimenId')}`)) {
        return;
      }
      await this.copyFromRegimen(regimen);
    } catch (err) {
      alert(err.message || err);
    }
  }

  async copyFromRegimen(regimen: any, skipTimeout?: boolean): Promise<void> {
    const fieldsToSkip = [
      'regimenId',
      'class',
      'type',
      'objectId',
      'ACL',
      'endDate',
      'activeFromDate',
      'createdAt',
      'updatedAt',
      'active',
      'followUpConfig',
      'numberOfProductsLimit',
      'followUpSop',
      'disableFollowUpSop',
    ];
    if (this.regimenObj.has('forUser')) {
      fieldsToSkip.push('fixedPrice', 'fixedPriceMRP', 'invoiceTitle');
    }
    [].concat(Object.keys(JSON.parse(JSON.stringify(this.regimenObj))))
      .concat(Object.keys(JSON.parse(JSON.stringify(regimen))))
      .filter((key: string) => !fieldsToSkip.includes(key))
      .forEach((key: string) => this.regimenObj.set(key, regimen.get(key)));
    if (skipTimeout) return;
    const currentRegimen = this.regimenObj;
    this.regimenObj = new Table.Regimen();
    setTimeout(() => {
      this.regimenObj = currentRegimen;
      this.updatePrice();
    }, 0);
  }


  private fetchDietAndLifestyles(): void {
    this.conn.fetchDietAndLifestyles()
      .then((items: Array<any>) => (this.dietAndLifestyleList = items.map((item: any) => ({
        value: item.id,
        parseValue: item,
        display: item.get('titleLanguageString').get('en'),
      }))));
  }

  private removeUpdatedFlag(): void {
    const products = []
      .concat(...this.regimenObj.get('morning'))
      .concat(...this.regimenObj.get('night'));
    products.forEach((each_: any) => {
      const each = each_;
      delete each.updated;
    });
  }

  openRegimenProductChangeHistory(): void {
    this.dialog.open(RegimenProductChangeHistoryComponent, {
      width: '100%',
      data: { regimen: this.regimenObj },
    });
  }

  // variant changes
  copyRegimenFrom(copyTo_: any, copyFrom: any, updateDefaultValue: boolean = false): void {
    const copyTo = copyTo_;
    const copyFromRegimen = JSON.parse(JSON.stringify(copyFrom));
    Object.keys(copyFromRegimen).forEach((each: string) => {
      copyTo.set(each, copyFrom.get(each));
    });
    if (!updateDefaultValue) return;
    copyTo.unset('fixedPriceMRP');
    copyTo.unset('followUpConfig');
    copyTo.unset('fixedPrice');
    copyTo.unset('numberOfProductsLimit');
    copyTo.unset('regimenCoverImage');
    copyTo.set('tags', []);
    copyTo.set('morning', []);
    copyTo.set('night', []);
    copyTo.set('products', []);
  }

  async updateVariantsDetails(): Promise<void> {
    if (this.regimenObj.get('type') === ApiClientConstant.Regimen.Type.PERSONALIZED || !this.regimenObj.id) {
      return;
    }
    this.regimenObjVariant1 = this.regimenObj;
    this.regimenObj = this.regimenObjVariant1;
    this.regimenObjVariant2 = new Table.Regimen();
    this.regimenObjVariant3 = new Table.Regimen();
    this.regimenObjVariant4 = new Table.Regimen();

    this.copyRegimenFrom(this.regimenObjVariant2, this.regimenObj, true);
    this.updateRegimenObjVariant(this.regimenObjVariant2, this.appConfig.Shared.Regimen.Variants.VariantId.variant_1999);
    this.copyRegimenFrom(this.regimenObjVariant3, this.regimenObj, true);
    this.updateRegimenObjVariant(this.regimenObjVariant3, this.appConfig.Shared.Regimen.Variants.VariantId.variant_4999);
    this.copyRegimenFrom(this.regimenObjVariant4, this.regimenObj, true);
    this.updateRegimenObjVariant(this.regimenObjVariant4, this.appConfig.Shared.Regimen.Variants.VariantId.variant_999);
    this.variants = this.regimenObj.get('variants')?.length ? this.regimenObj.get('variants') : this.variants;
  }

  updateRegimenObjVariant(regimenObjVariant: any, variantId: string): void {
    let variant = this.regimenObj.get('variants')?.find((each: any) => each.variantId === variantId);
    if (!variant) {
      variant = this.variants.find((each: any) => each.variantId === variantId);
    }
    regimenObjVariant.set('showAfterDays', variant.showAfterDays);
    regimenObjVariant.set('shouldDisableCOD', variant.shouldDisableCOD);
    regimenObjVariant.set('fixedPriceMRP', variant.fixedPriceMRP);
    regimenObjVariant.set('followUpConfig', variant.followUpConfig);
    regimenObjVariant.set('fixedPrice', variant.fixedPrice);
    regimenObjVariant.set('numberOfProductsLimit', variant.numberOfProductsLimit);
    regimenObjVariant.set('tags', variant.tags);
    regimenObjVariant.set('morning', variant.morning);
    regimenObjVariant.set('night', variant.night);
    regimenObjVariant.set('products', variant.products);
    regimenObjVariant.set('followUpSop', variant.followUpSop || []);
  }
  updateVariantsCombinedId(): void {
    this.variants.forEach((variant_: any) => {
      const variant = variant_;
      variant.combinedProductIds = this.getCombinedProductIds(variant.products);
    });
  }

  async changeVariant(variant: ValueOf<typeof this.appConfig.Shared.Regimen.Variants.VariantId>): Promise<void> {
    if (this.currentVariantId === variant) {
      return;
    }
    const variants2RegimenCoverImage = await this.getImageUrlforCombinedProduct(this.variants[1]?.combinedProductIds);
    this.regimenObjVariant2.set('regimenCoverImage', variants2RegimenCoverImage);
    const variants3RegimenCoverImage = await this.getImageUrlforCombinedProduct(this.variants[2]?.combinedProductIds);
    this.regimenObjVariant3.set('regimenCoverImage', variants3RegimenCoverImage);
    const variants4RegimenCoverImage = await this.getImageUrlforCombinedProduct(this.variants[3]?.combinedProductIds);
    this.regimenObjVariant3.set('regimenCoverImage', variants4RegimenCoverImage);
    this.currentVariantId = variant;
    switch (this.currentVariantId) {
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_1499: {
        this.regimenObj = this.regimenObjVariant1;
        this.followUpConfig = this.regimenObjVariant1.get('followUpConfig');
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_1999: {
        this.regimenObj = this.regimenObjVariant2;
        this.followUpConfig = this.regimenObjVariant2.get('followUpConfig');
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_4999: {
        this.regimenObj = this.regimenObjVariant3;
        this.followUpConfig = this.regimenObjVariant3.get('followUpConfig');
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_999: {
        this.regimenObj = this.regimenObjVariant4;
        this.followUpConfig = this.regimenObjVariant4.get('followUpConfig');
        break;
      }
      default:
    }
    if (!this.followUpConfig) {
      this.followUpConfig = {
        intervalInDays: 28,
        durationInDays: 90,
      };
    }
    this.changeMorningProducts = !this.changeMorningProducts;
    this.changeNightProducts = !this.changeNightProducts;
    const currentVariant = this.variants.find((each: any) => each.variantId === this.currentVariantId);
    this.disableVariant = currentVariant?.disable || false;
  }

  async updateVariants(): Promise<boolean> {
    if (this.regimenObjVariant1?.get('morning')?.length || this.regimenObjVariant1?.get('night')?.length) {
      this.regimenObj = this.regimenObjVariant1;
      this.currentVariantId = ApiClientConstant.Regimen.SelectedVariantId.variant_1499;
    }
    if (this.regimenObj.get('type') === ApiClientConstant.Regimen.Type.PERSONALIZED
      || !this.variants.length) {
      return true;
    }
    this.updateVariantFromRegimen(this.appConfig.Shared.Regimen.Variants.VariantId.variant_1499, this.regimenObjVariant1);
    this.updateVariantFromRegimen(this.appConfig.Shared.Regimen.Variants.VariantId.variant_1999, this.regimenObjVariant2);
    this.updateVariantFromRegimen(this.appConfig.Shared.Regimen.Variants.VariantId.variant_4999, this.regimenObjVariant3);
    this.updateVariantFromRegimen(this.appConfig.Shared.Regimen.Variants.VariantId.variant_999, this.regimenObjVariant4);
    this.updateVariantsCombinedId();
    const isValidCombinedId = await this.checkVariantCombinedId();
    if (!isValidCombinedId) {
      this.broadcast.broadcast('NOTIFY', { message: 'Not a valid CombinedId',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return false;
    }
    const filteredVariants = this.variants.filter((variant:any) => variant.products?.length);
    this.regimenObj.set('variants', filteredVariants);
    return true;
  }

  async checkVariantCombinedId(): Promise<boolean> {
    const variantsCombinedIds = await this.conn.findMediaLink({
      where: {
        uniqueId: this.variants.map((each: any) => each.combinedProductIds),
      },
    });
    if (!variantsCombinedIds.length) {
      return false;
    }
    const variantsCombinedObjectIds = variantsCombinedIds.map((each: any) => each.get('uniqueId'));
    const missingCombinedIds = [];
    this.variants.forEach((each: any) => {
      if (!variantsCombinedObjectIds.includes(each.combinedProductIds) && each.combinedProductIds) {
        missingCombinedIds.push(each.variantId);
      }
    });
    if (missingCombinedIds.length) {
      this.broadcast.broadcast('NOTIFY', { message: `combinedProductIds not found ${missingCombinedIds}`,
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return false;
    }
    return true;
  }

  updateVariantFromRegimen(variantId: string, regimen: any): void {
    if (!regimen || !regimen.get('morning')?.length) return;
    let index = this.variants.findIndex((variant: any) => variant.variantId === variantId);
    if (index === -1) {
      this.variants.push({ variantId });
      index = this.variants.findIndex((variant: any) => variant.variantId === variantId);
    }
    this.variants[index].fixedPriceMRP = regimen.get('fixedPriceMRP');
    this.variants[index].followUpConfig = regimen.get('followUpConfig');
    this.variants[index].fixedPrice = regimen.get('fixedPrice');
    this.variants[index].combinedProductIds = regimen.get('combinedProductIds');
    this.variants[index].numberOfProductsLimit = regimen.get('numberOfProductsLimit');
    this.variants[index].tags = regimen.get('tags');
    this.variants[index].morning = regimen.get('morning');
    this.variants[index].night = regimen.get('night');
    this.variants[index].products = this.getAllUniqueProducts([...regimen.get('morning'), ...regimen.get('night')]);
    this.variants[index].followUpSop = regimen.get('followUpSop') || [];
  }

  updateVariantFollowUpConfig(): void {
    switch (this.currentVariantId) {
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_1499: {
        this.regimenObjVariant1.set('followUpConfig', this.followUpConfig);
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_1999: {
        this.regimenObjVariant2.set('followUpConfig', this.followUpConfig);
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_4999: {
        this.regimenObjVariant3.set('followUpConfig', this.followUpConfig);
        break;
      }
      case this.appConfig.Shared.Regimen.Variants.VariantId.variant_999: {
        this.regimenObjVariant4.set('followUpConfig', this.followUpConfig);
        break;
      }
      default:
    }
  }

  copyRegimenFromRegimen(regimenVariantFrom: any): void {
    const morningProducts = regimenVariantFrom.get('morning').map((morningProduct: any) => ({ ...morningProduct }));
    const nightProducts = regimenVariantFrom.get('night').map((nightProduct: any) => ({ ...nightProduct }));
    this.regimenObj.set('morning', morningProducts);
    this.regimenObj.set('night', nightProducts);
    this.changeMorningProducts = !this.changeMorningProducts;
    this.changeNightProducts = !this.changeNightProducts;
  }

  getAllUniqueProducts(products: Array<any> = []): Array<any> {
    if (!products.length) return [];
    const addedProductsIds = [];
    const uniqueProducts = [];
    products.forEach((product: any) => {
      if (!addedProductsIds.includes(product.product.id)) {
        addedProductsIds.push(product.product.id);
        uniqueProducts.push(product.product);
      }
    });
    return uniqueProducts;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    delete this.router;
    delete this.appConfig;
    delete this.conn;
    delete this.route;
    delete this.subscriptions;
    delete this.regimenObj;
  }

  private isSameArrayOfParseObject(parseObjectArray1: Array<any> = [], parseObjectArray2: Array<any> = []): boolean {
    if (parseObjectArray1.length !== parseObjectArray2.length) {
      return false;
    }
    return parseObjectArray1.every((item: any, index: number) => (item.id === parseObjectArray2[index].id));
  }

  updateVariantIsDisable(): void {
    const index = this.variants
      .findIndex((variant: any) => variant.variantId === this.currentVariantId);
    if (index === -1) return;
    this.variants[index].disable = this.disableVariant;
  }

  deleteFollowUp(index: any): any {
    this.regimenObj.get('followUpSop').splice(index, 1);
  }

  changeSelectedChatType(selectChat: boolean): void {
    if (selectChat) {
      this.selectedChatType = 'userChat';
      return;
    }
    this.selectedChatType = 'chatSummary';
  }

  markOrderWaitingForImageNew(): void {
    this.broadcast.broadcast('NOTIFY', { message: 'Can\'t be marked as waiting for image',
      type: this.appConfig.Shared.Toast.Type.ERROR });
  }

  private getConfirmationMessage(productsHasChanged: boolean): string {
    const confirmationMessage1 = 'Products are updated, do you want to approve regimen?';
    const confirmationMessage2 = 'No products are updated, do you want to approve regimen?';
    return productsHasChanged ? confirmationMessage1 : confirmationMessage2;
  }

  private async saveRegimenDrVerifiedState(): Promise<void> {
    try {
      this.regimenObj.set('doctorVerified', true);
      await this.regimenObj.save({}, { context: { placeOrderForNewProduct: false } });
      this.broadcast.broadcast('NOTIFY', { message: 'Regimen Updated', type: this.appConfig.Shared.Toast.Type.SUCCESS });
    } catch (err) {
      this.broadcast.broadcast('NOTIFY', {
        message: err.toString(),
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
    }
  }

  async approveRegimen(): Promise<void> {
    const productsHasChanged = this.regimenObj.dirtyKeys()?.length !== 0;
    const confirmationMessage = this.getConfirmationMessage(productsHasChanged);

    if (confirm(confirmationMessage)) {
      await this.saveRegimenDrVerifiedState();
    }
  }

  constructProductName(product: any): string {
    return `${product.get('title')} [${product.get('quantity')}${product.get('quantityUnit')}] (${product.get('type')})`;
  }

  initProgressiveInfoForm() {
    this.progressiveInfoForm = this.fb.group({
      product: [null],
      action: [ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER],
      startAfterDayOfDelivery: [null],
      endAfterDayOfDelivery: [null],
      status: [null],
      productInfo: this.fb.group({
        instruction: [null],
        onHold: [false],
        purposeDescriptionLanguageString: [null],
        purposeLanguageString: [null],
      }),
      availableInstructions: [[]],
      selectedInstructionSet: [null]
    });
    this.updateProgressiveInfoFormState();
    this.progressiveInfoForm.get('action').valueChanges.subscribe(action => {
      this.showProgressiveProductInfo =
        action === ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER_WITH_REGIMEN_CHANGE;

      if (this.showProgressiveProductInfo && this.progressiveInfoForm.get('product').value) {
        this.renderProductInstructions();
      }
    });
    if (this.regimenObj && this.regimenObj.get('progressiveInfo')?.length > 0) {
      const progressiveInfo = this.regimenObj.get('progressiveInfo')[0];
      this.progressiveInfoForm.patchValue({
        action: progressiveInfo.action,
        startAfterDayOfDelivery: progressiveInfo.startAfterDayOfDelivery,
        endAfterDayOfDelivery: progressiveInfo.endAfterDayOfDelivery,
        status: progressiveInfo.status
      });
      // Load product info if available
      if (progressiveInfo.productInfo) {
        this.progressiveInfoForm.get('productInfo').patchValue({
          onHold: progressiveInfo.productInfo.onHold || false,
          purposeDescriptionLanguageString: progressiveInfo.productInfo.purposeDescriptionLanguageString,
          purposeLanguageString: progressiveInfo.productInfo.purposeLanguageString,
        });
      }
      if (progressiveInfo.product) {
        this.loadProgressiveInfoProduct(progressiveInfo.product);
      }
      if (progressiveInfo?.productInfo?.instructionSet) {
        this.progressiveInfoForm.get('selectedInstructionSet').setValue(progressiveInfo.productInfo.instructionSet);
      }
    }
  }

  updateProgressiveInfoFormState(): void {
    if (!this.progressiveInfoForm) return;

    if (this.disableProgressiveInfo) {
      // Disable form controls
      this.autoCompleteProgessiveInfo.disable();
      this.progressiveInfoForm.get('action').disable();
      this.progressiveInfoForm.get('startAfterDayOfDelivery').disable();
      this.progressiveInfoForm.get('endAfterDayOfDelivery').disable();
      this.progressiveInfoForm.get('productInfo').disable();
    } else {
      // Enable form controls
      this.autoCompleteProgessiveInfo.enable();
      this.progressiveInfoForm.get('action').enable();
      this.progressiveInfoForm.get('startAfterDayOfDelivery').enable();
      this.progressiveInfoForm.get('endAfterDayOfDelivery').enable();
      this.progressiveInfoForm.get('productInfo').enable();
    }
  }

  async loadProgressiveInfoProduct(productPointer) {
    this.progressiveInfoInstructionLoading = true;
    this.progressiveInfoProductObj = await productPointer.fetch();
    this.progressiveInfoForm.patchValue({ product: productPointer });
    // Set purpose description if available
    if (this.progressiveInfoProductObj.get('purposeDescriptionLanguageString')) {
      this.progressiveInfoForm.get('productInfo.purposeDescriptionLanguageString').setValue(
        this.progressiveInfoProductObj.get('purposeDescriptionLanguageString')
      );
    }
    const productName = this.constructProductName(this.progressiveInfoProductObj);
    this.autoCompleteProgessiveInfo.setValue(productName);
    await this.renderProductInstructions();
    const existingInfo = this.regimenObj.get('progressiveInfo')?.[0]?.productInfo;
    if (existingInfo?.instructionSet?.id) {
    const instructions = this.progressiveInfoForm.get('availableInstructions').value;
    const matchingInstruction = instructions.find(i =>
      i.parseValue.id === existingInfo.instructionSet.id);
    if (matchingInstruction) {
      this.progressiveInfoForm.get('productInfo.instruction').setValue(matchingInstruction.value);
      this.progressiveInfoForm.get('selectedInstructionSet').setValue(matchingInstruction.parseValue);
      }
    }
    this.progressiveInfoInstructionLoading = false;
  }

  async renderProductInstructions(): Promise<void> {
    if (this.progressiveInfoForm.get('action').value !== ApiClientConstant.ProgressiveTreatment.Action.ADD_TO_BANNER_WITH_REGIMEN_CHANGE) return;
    // Get instructions from the product
    const instructions = this.progressiveInfoProductObj.get('mainProduct').get('instructions').map((instruction: any) => {
      return {
        display: instruction.title,
        value: instruction.id,
        parseValue: instruction,
      };
    });
    // Store available instructions in the form
    this.progressiveInfoForm.get('availableInstructions').setValue(instructions);
    if (instructions.length > 0) {
      // Set default instruction if not already set
      if (!this.progressiveInfoForm.get('productInfo.instruction').value) {
        this.progressiveInfoForm.get('productInfo.instruction').setValue(instructions[0].value);
        this.progressiveInfoForm.get('selectedInstructionSet').setValue(instructions[0].parseValue);
      } else {
        // Find and set the selected instruction
        const selectedInstructionId = this.progressiveInfoForm.get('productInfo.instruction').value;
        const selectedInstruction = instructions.find(i => i.value === selectedInstructionId);
        if (selectedInstruction) {
          this.progressiveInfoForm.get('selectedInstructionSet').setValue(selectedInstruction.parseValue);
        }
      }
    }
    this.changedetectionRef.detectChanges();
  }

  onProgressiveInfoProductInstructionChange(event: any): void {
    const instructionId = event.target.value;
    // Find the selected instruction from available instructions
    const instructions = this.progressiveInfoForm.get('availableInstructions').value;
    const selectedInstruction = instructions.find(i => i.value === instructionId);
    if (selectedInstruction) {
      // Update the selected instruction set in the form
      this.progressiveInfoForm.get('selectedInstructionSet').setValue(selectedInstruction.parseValue);
    }
  }

  async autoCompleteProgressiveInfoOnSelect(option: any) {
    this.progressiveInfoInstructionLoading = true;
    // Reset instruction-related fields first
    this.progressiveInfoForm.get('productInfo.instruction').setValue(null);
    this.progressiveInfoForm.get('selectedInstructionSet').setValue(null);
    this.progressiveInfoForm.get('availableInstructions').setValue([]);
    // Fetch the new product
    this.progressiveInfoProductObj = await option.object.fetch();
    const catalogPointer = option.object.toPointer();
    // Update the form with the new product
    this.progressiveInfoForm.patchValue({
      product: catalogPointer,
      productInfo: {
        purposeDescriptionLanguageString: this.progressiveInfoProductObj.get('purposeDescriptionLanguageString'),
        purposeLanguageString: this.progressiveInfoProductObj.get('purposeLanguageString')
      }
    });
    // Load the instructions for the new product
    await this.renderProductInstructions();
    this.progressiveInfoInstructionLoading = false;
  }

  onProgressiveInfoInputChange(event: any): void {
    const inputValue = event.target.value;
    if (!inputValue || inputValue.trim() === '') {
      // Reset the entire form to initial values
      this.progressiveInfoForm.reset();
      this.progressiveInfoProductObj = null;
      this.showProgressiveProductInfo = false; // Hide the product info section
      return;
    }
    // If the input value doesn't match the currently selected product, reset the instructions
    const currentProductName = this.progressiveInfoProductObj ?
      this.constructProductName(this.progressiveInfoProductObj) : '';
    if (inputValue !== currentProductName) {
      // Reset instruction-related fields
      this.progressiveInfoForm.get('productInfo.instruction').setValue(null);
      this.progressiveInfoForm.get('selectedInstructionSet').setValue(null);
      this.progressiveInfoForm.get('availableInstructions').setValue([]);
    }
  }

  async updateVoiceNote(): Promise<void> {
    const uniqueAudioTemplateName = `${this.regimenObj.get('regimenId')}_approval_voice_note`;
    this.dialog.open(VoiceNoteAddDialog, { data: { audioTemplateName: uniqueAudioTemplateName, source: 'regimen_approval_voice_note', type: 'regimen_voice_note' } })
      .afterClosed().subscribe(async (result: any) => {
        this.regimenObj.set('regimenVoiceNote', result);
      });
  }

  async fetchTickets(): Promise<Array<Table.SupportTicket>> {
    const tickets = await this.conn.fetchSupportTickets({
      where: {
        user: this.regimenObj.get('forUser'),
        status: 'Pending',
        teams: { $in: [ApiClientConstant.Role.Name.DOCTOR] },
      },
      include: ['allocatedDoctor', 'titleLanguageString'],
    });
    return tickets;
  }

  async openOnHoldPopup(): Promise<boolean> {
    const tickets = await this.fetchTickets();
    const dialogRef = this.dialog.open(OnHoldPopupComponent, {
      data: {
        tickets,
        onHoldType: OnHoldDetailsOnHoldTypeEnum.TICKET,
      },
    });
    return new Promise<boolean>((resolve) => {
      dialogRef.afterClosed().subscribe(async (result: any) => {
        if (result && result.numberOfDays && result.ticketId) {
          this.onHoldProductsPayload.onHoldDurationInDays = result.numberOfDays;
          this.onHoldProductsPayload.ticketId = result.ticketId;
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  async upsertOnHoldProducts(
    regimenObjectId: string,
    onHoldProductsInput: ServerAPIRequest['OnHoldProducts'],
  ): Promise<ServerAPIResponse['UpsertOnHoldProducts']> {
    const userActivityData = await this.conn.serverApi.upsertOnHoldProductsInRegimen(
        this.conn.getCurrentUser().getSessionToken(),
        regimenObjectId,
        onHoldProductsInput,
      );
    return userActivityData;
  }

  updateRegimenFromAPIResponse(morningCopy: any[] = [], nightCopy: any[] = [], morning: any[] = [], night: any[] = []): void {
    const getProductId = (item: any): string => (item?.productId || item?.product?.id || item?.product?.objectId);
    const getPartOfDay = (item: any): string => (item?.partOfDay || item?.instructionSet?.partOfDay);
    const makeKey = (item: any): string => `${getProductId(item)}__${getPartOfDay(item)}`;

    const updatedKeys = new Set(
      (this.onHoldProductsPayload?.onHoldProducts || [])
        .map((p: any) => `${p?.productId}__${p?.partOfDay}`)
    );
    // Build final arrays from local regimen; replace on-hold matches with API items
    const rebuildFromLocal = (apiArr: any[], localArr: any[]): void => {
      if (!Array.isArray(apiArr) || !Array.isArray(localArr)) return;
      const apiMap = new Map<string, any>(apiArr.map((s: any) => [makeKey(s), s]));
      const finalArr: any[] = [];
      for (let i = 0; i < localArr.length; i += 1) {
        const localItem = localArr[i];
        const key = makeKey(localItem);
        if (updatedKeys.has(key) && apiMap.has(key)) {
          // For on-hold items, replace with API item entirely to keep server fields
          finalArr.push({...apiMap.get(key), instructionSet: localItem.instructionSet, product: localItem.product});
        } else {
          finalArr.push(localItem);
        }
      }
      apiArr.length = 0;
      for (let i = 0; i < finalArr.length; i += 1) apiArr.push(finalArr[i]);
    };

    rebuildFromLocal(morningCopy, morning);
    rebuildFromLocal(nightCopy, night);
  }

  async showFollowUpSopHistory(): Promise<void> {
    this.dialog.open(FollowUpSopHistoryComponent, {
      width: '80%',
      data: { regimen: this.regimenObj },
    });
  }

  addFollowUp(): void {
    if (!this.regimenObj.get('followUpSop')) {
      this.regimenObj.set('followUpSop', []);
    }
    this.regimenObj.get('followUpSop').push({
      name: '',
      shortName: '',
      tests: {},
      disabled: false,
      changes: [],
      nextFollowUps: [],
      voiceNote: {},
      treatmentProgress: [],
    });
  }

  async copyFollowUpSop(regimenObj: Table.Regimen): Promise<void> {
    const copiedFollowUpSop: Table.Regimen['attributes']['followUpSop'] = this.helperService.deepCopy(regimenObj.get('followUpSop'));
    await this.updateFollowUpSopForCurrentRegimen(copiedFollowUpSop as Array<FollowUpSop>);
    this.regimenObj.set('followUpSop', copiedFollowUpSop);
  }

  async updateFollowUpSopForCurrentRegimen(followUpSop: Array<FollowUpSop>, level: number = 0): Promise<void> {
    if (!followUpSop?.length) {
      return;
    }
    await Promise.all([followUpSop.map((each) => {
      const uniqueAudioTemplateName = [this.regimenObj.get('regimenId'), each.name, level];
      if (this.currentVariantId !== ApiClientConstant.Regimen.SelectedVariantId.variant_1499) {
        uniqueAudioTemplateName.push(this.currentVariantId);
      }
      this.copyAudioTemplateName(each.audioTemplateName, uniqueAudioTemplateName.join('_'))
        .then((audioTemplateName) => each.audioTemplateName = audioTemplateName);
    })]);
    await Promise.all(followUpSop.map((each) => {
      this.updateFollowUpSopForCurrentRegimen(each.nextFollowUps, level+1);
    }))
  }

  private unsetAudioTemplateNameFromFollowUpSop(followUpSop: Table.Regimen['attributes']['followUpSop']): void {
    if (!followUpSop?.length) {
      return;
    }
    followUpSop.forEach((each) => {
      each.audioTemplateName = '';
      this.unsetAudioTemplateNameFromFollowUpSop(each.nextFollowUps);
    });
  }

  private checkInstructionsExists(): void {
    if ([...this.regimenObj.get('morning'), ...this.regimenObj.get('night')].filter((product) => !product.instructionSet).length) {
      this.broadcast.broadcast('NOTIFY', { message: 'Instructions missing', type: this.appConfig.Shared.Toast.Type.ERROR});
      throw new Error('Instructions missing');
    }
  }

  private async copyAudioTemplateName(fromAudioTemplateName: string, toAudioTemplateName: string): Promise<string> {
    const audioTemplate = await this.conn.findAudioTemplateByName(fromAudioTemplateName, 'en');
    let newAudioTemplate = new Table.AudioTemplate();
    const toAudioTemplate = await this.conn.findAudioTemplateByName(toAudioTemplateName, 'en');
    if (toAudioTemplate) {
      newAudioTemplate = toAudioTemplate;
    }
    const newUrlLanguageString =  audioTemplate.get('urlLanguageString').clone();
    await newUrlLanguageString.save();
    await newAudioTemplate.save({ name: toAudioTemplateName, urlLanguageString: newUrlLanguageString, type: 'followUpSop' });
    return toAudioTemplateName;
  }

  private syncMainRegimenValuesToVariant(): void {
    if (this.regimenObj.get('type') === ApiClientConstant.Regimen.Type.PERSONALIZED) {
      return;
    }
    this.variants.forEach((variant) => {
      variant.tags = this.regimenObj?.get('tags');
    });
  }

  private resetFollowUpSopAudioTemplate() {
    this.unsetAudioTemplateNameFromFollowUpSop(this.regimenObj.get('followUpSop'));
    if (!this.regimenObj.get('variants')?.length) {
      return;
    }
    this.regimenObj.get('variants').forEach((variant: Table.Regimen['attributes']['variants'][number]) => {
      this.unsetAudioTemplateNameFromFollowUpSop(variant.followUpSop as Table.Regimen['attributes']['followUpSop']);
    })
  }
}
